/**
 * Shared TypeBox schemas for primitive types used across all entities.
 *
 * These schemas produce standard JSON Schema and derive TypeScript types via Static<>.
 */

import { Type, type Static } from 'typebox'

/**
 * Mapped type that adds `| undefined` to every optional property.
 * Required for `exactOptionalPropertyTypes` compatibility — TypeBox's
 * `Type.Optional()` produces `field?: T` but the tsconfig requires
 * `field?: T | undefined` for `{ field: undefined }` to be assignable.
 *
 * Apply this to any `Static<typeof Schema>` export.
 */
export type Exact<T> = T extends readonly [...infer _Items]
  ? { [K in keyof T]: Exact<T[K]> } // preserve tuples
  : T extends unknown[]
    ? Exact<T[number]>[] // arrays
    : T extends object
      ? {
          [K in keyof T as undefined extends T[K] ? never : K]: Exact<T[K]>
        } & {
          [K in keyof T as undefined extends T[K] ? K : never]?: Exact<T[K]> | undefined
        }
      : T

/** UUID format — accepts any version (v1-v7+, Cognito uses v7-style subs) */
export const UUID = Type.String({
  pattern: '^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$',
})

/** ISO 8601 date-time string */
export const ISODate = Type.String({ format: 'date-time' })

/** Hex color: #RGB or #RRGGBB */
export const HexColor = Type.String({
  pattern: '^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$',
})

/** Coordinate pair [lat, lng] */
export const Coordinates = Type.Tuple([
  Type.Number({ minimum: -90, maximum: 90 }),
  Type.Number({ minimum: -180, maximum: 180 }),
])
export type Coordinates = Static<typeof Coordinates>

/** Geographic bounding box */
export const Bounds = Type.Object({
  ne: Coordinates,
  sw: Coordinates,
})
export type Bounds = Static<typeof Bounds>

/** Email address (matches ValidationUtils.isValidEmail regex) */
export const Email = Type.String({
  pattern: '^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$',
})
